import type { BrandKey } from '../../data/home';
import i18n from '../../i18n';
import { formatNumber } from '../../i18n/format';

export const CATEGORIES = ['All', 'Shopping', 'Entertainment', 'Gaming', 'Travel'] as const;
export const COUNTRIES = ['United States', 'Vietnam', 'Singapore'] as const;

export type Category = (typeof CATEGORIES)[number];
export type Country = (typeof COUNTRIES)[number];
export type CatalogBrand = Exclude<BrandKey, 'deposit'>;

export type ProductDenomination = {
    id: string;
    value: number;
    currency: string;
    labelEn?: string;
    labelCn?: string;
    labelVi?: string;
    labelTh?: string;
    labelJa?: string;
    labelKo?: string;
    labelId?: string;
    labels?: Partial<Record<string, string>>;
    available?: boolean;
};

export type CatalogProduct = {
    id: string;
    name: string;
    nameEn?: string;
    category: string;
    region?: string;
    regionName?: string;
    country?: string;
    currency?: string;
    description?: string;
    descriptionCn?: string;
    descriptions?: Record<string, string>;
    imageUrl?: string;
    logoUrl?: string;
    brandCode?: string;
    catalog?: string;
    deliveryType?: string;
    badge?: string | null;
    isAvailable?: boolean;
    denominations?: ProductDenomination[];
    /** Legacy mock fields */
    from?: number;
    word?: string;
    denoms?: number[];
};

export function getAvailableDenoms(product: CatalogProduct): ProductDenomination[] {
    if (Array.isArray(product.denominations) && product.denominations.length) {
        return product.denominations.filter((item) => item.available !== false);
    }

    return (product.denoms || []).map((value, index) => ({
        id: `${product.id}-${value}-${index}`,
        value,
        currency: product.currency || 'USD',
        labelEn: `${product.currency || 'USD'} ${value}`,
        available: true,
    }));
}

export function getDenomValues(product: CatalogProduct): number[] {
    return getAvailableDenoms(product).map((item) => item.value);
}

const DENOM_LABEL_FIELDS: Record<string, keyof ProductDenomination> = {
    en: 'labelEn',
    zh: 'labelCn',
    cn: 'labelCn',
    vi: 'labelVi',
    th: 'labelTh',
    ja: 'labelJa',
    ko: 'labelKo',
    id: 'labelId',
};

function readDenomText(value: unknown) {
    return typeof value === 'string' ? value.trim() : '';
}

export function isEsimProduct(product: CatalogProduct) {
    const hint = [product.catalog, product.category, product.brandCode, product.id]
        .filter((value): value is string => Boolean(value))
        .join(' ')
        .toLowerCase();
    if (hint.includes('esim')) return true;
    return (product.denominations || []).some((item) => item.id.toLowerCase().includes('esim'));
}

export function getDenomPlanLabel(denom: ProductDenomination, language?: string) {
    const lang = (language || i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
    const languageKey = lang.startsWith('zh') ? 'zh' : lang.split('-')[0];
    const labelKeys = languageKey === 'zh' ? ['zh', 'cn'] : [languageKey];

    for (const key of labelKeys) {
        const fromMap = readDenomText(denom.labels?.[key]);
        if (fromMap) return fromMap;
        const field = DENOM_LABEL_FIELDS[key];
        const specific = field ? readDenomText(denom[field]) : '';
        if (specific) return specific;
    }

    return readDenomText(denom.labels?.en) || readDenomText(denom.labelEn) || readDenomText(denom.labelCn);
}

export function getProductDisplayName(product: CatalogProduct): string {
    return product.nameEn || product.name;
}

const DESCRIPTION_KEYS: Record<string, string[]> = {
    en: ['en'],
    vi: ['vi'],
    th: ['th'],
    ja: ['ja'],
    ko: ['ko'],
    id: ['id'],
    'zh-cn': ['zh', 'cn'],
    zh: ['zh', 'cn'],
    cn: ['cn', 'zh'],
};

export function getProductDescription(product: CatalogProduct, language?: string): string {
    const lang = (language || i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
    const descriptions = product.descriptions;
    const keys = DESCRIPTION_KEYS[lang] || DESCRIPTION_KEYS[lang.split('-')[0]] || [lang];

    if (descriptions) {
        for (const key of keys) {
            const value = descriptions[key];
            if (typeof value === 'string' && value.trim()) return value.trim();
        }
        const english = descriptions.en;
        if (typeof english === 'string' && english.trim()) return english.trim();
    }

    if (keys.some((key) => key === 'zh' || key === 'cn') && product.descriptionCn?.trim()) {
        return product.descriptionCn.trim();
    }

    return product.description?.trim() || '';
}

export function getProductDetailPath(product: CatalogProduct) {
    return `/explore?id=${encodeURIComponent(product.id)}`;
}

export function getProductLogo(product: CatalogProduct) {
    return product.imageUrl || product.logoUrl || '';
}

export function formatDenomRange(product: CatalogProduct): string {
    const denoms = getAvailableDenoms(product);
    if (!denoms.length) return i18n.t('catalog.giftCardFallback', { ns: 'explore' });

    const currency = denoms[0].currency || product.currency || '';
    const min = denoms[0].value;
    const max = denoms[denoms.length - 1].value;

    if (min === max) return `${currency} ${formatNumber(min)}`.trim();
    return `${currency} ${formatNumber(min)}–${formatNumber(max)}`.trim();
}

export function formatDenomFrom(product: CatalogProduct): string {
    const denoms = getAvailableDenoms(product);
    if (!denoms.length) {
        if (product.from != null) {
            return `${product.currency || ''} ${formatNumber(product.from)}`.trim();
        }
        return '—';
    }

    const currency = denoms[0].currency || product.currency || '';
    return `${currency} ${formatNumber(denoms[0].value)}`.trim();
}

export const CATALOG: CatalogProduct[] = [
    {
        id: 'amazon',
        name: 'Amazon',
        category: 'Shopping',
        from: 10,
        word: 'amazon',
        description: 'A little something. Or absolutely everything.',
        denoms: [10, 25, 50, 100],
    },
    {
        id: 'apple',
        name: 'Apple',
        category: 'Entertainment',
        from: 10,
        word: 'Apple',
        description: 'Apps, games, music and more.',
        denoms: [10, 25, 50, 100],
    },
    {
        id: 'steam',
        name: 'Steam',
        category: 'Gaming',
        from: 10,
        word: 'STEAM',
        description: 'Your next adventure starts here.',
        denoms: [10, 25, 50, 100],
    },
    {
        id: 'uber',
        name: 'Uber',
        category: 'Travel',
        from: 15,
        word: 'Uber',
        description: 'A ride to wherever comes next.',
        denoms: [15, 25, 50, 100],
    },
    {
        id: 'grab',
        name: 'Grab',
        category: 'Travel',
        from: 10,
        word: 'Grab',
        description: 'Everyday rides and deliveries.',
        denoms: [10, 25, 50, 100],
    },
    {
        id: 'spotify',
        name: 'Spotify',
        category: 'Entertainment',
        from: 10,
        word: 'Spotify',
        description: 'Give your day a soundtrack.',
        denoms: [10, 25, 50, 100],
    },
];

export function formatPrice(value: number) {
    return formatNumber(value, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}

type PromoRule = {
    type: 'fixed' | 'percentage';
    value?: number;
    basisPoints?: number;
    cap?: number;
    min: number;
    expired?: boolean;
    accountOnly?: boolean;
};

const PROMO_RULES: Record<string, PromoRule> = {
    SAVE5: { type: 'fixed', value: 5, min: 20 },
    SAVE10: { type: 'percentage', basisPoints: 1000, cap: 5, min: 10 },
    ONCE3: { type: 'fixed', value: 3, min: 10 },
    ALEX5: { type: 'fixed', value: 5, min: 20, accountOnly: true },
    EXPIRED5: { type: 'fixed', value: 5, min: 0, expired: true },
};

export type PromoResult = {
    code: string | null;
    subtotal: number;
    discount: number;
    total: number;
    error?: string;
    errorValues?: { minimum?: number };
    accountOnly?: boolean;
};

export function calculatePromo(
    amount: number,
    rawCode: string | null,
    options: { displayName?: string; usedCodes?: string[] } = {}
): PromoResult {
    const subtotal = amount;
    const code = (rawCode || '').trim().toUpperCase();

    if (!code) {
        return { code: null, subtotal, discount: 0, total: subtotal };
    }

    const rule = PROMO_RULES[code];
    if (!rule) {
        return {
            code: null,
            subtotal,
            discount: 0,
            total: subtotal,
            error: 'promo.errors.unavailable',
        };
    }
    if (rule.expired) {
        return {
            code: null,
            subtotal,
            discount: 0,
            total: subtotal,
            error: 'promo.errors.expired',
        };
    }
    if (rule.accountOnly && !/alex/i.test(options.displayName || '')) {
        return {
            code: null,
            subtotal,
            discount: 0,
            total: subtotal,
            error: 'promo.errors.accountUnavailable',
        };
    }
    if (options.usedCodes?.includes(code)) {
        return {
            code: null,
            subtotal,
            discount: 0,
            total: subtotal,
            error: 'promo.errors.alreadyUsed',
        };
    }
    if (subtotal < rule.min) {
        return {
            code: null,
            subtotal,
            discount: 0,
            total: subtotal,
            error: 'promo.errors.minimumSpend',
            errorValues: { minimum: rule.min },
        };
    }

    let discount = rule.type === 'fixed' ? rule.value || 0 : (subtotal * (rule.basisPoints || 0)) / 10000;
    if (rule.cap != null) discount = Math.min(discount, rule.cap);
    discount = Math.min(subtotal, discount);

    if (discount <= 0) {
        return {
            code: null,
            subtotal,
            discount: 0,
            total: subtotal,
            error: 'promo.errors.noDiscount',
        };
    }

    return {
        code,
        subtotal,
        discount,
        total: subtotal - discount,
        accountOnly: rule.accountOnly,
    };
}
